import React, { useCallback, useState } from 'react';
import { PencilIcon, PlusIcon, TrashIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { STAFF_MEMBERS, type InternalTeam } from '../../data/internalTeams';
import { setInternalTeams, useInternalTeams } from '../../hooks/useInternalTeams';
import { InternalTeamFormDialog } from './InternalTeamFormDialog';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';

/** Configurer → Équipes internes: name + list of users per team. */
export function InternalTeamsConfig() {
  const {
    t
  } = useLanguage();
  const teams = useInternalTeams();
  const [form, setForm] = useState<{
    open: false;
  } | {
    open: true;
    team: InternalTeam | null;
  }>({
    open: false
  });
  const [toDelete, setToDelete] = useState<InternalTeam | null>(null);
  const closeForm = useCallback(() => setForm({
    open: false
  }), []);
  const cancelDelete = useCallback(() => setToDelete(null), []);
  const save = (saved: InternalTeam) => {
    const exists = teams.some(team => team.id === saved.id);
    setInternalTeams(exists ? teams.map(team => team.id === saved.id ? saved : team) : [...teams, saved]);
    closeForm();
  };
  const confirmDelete = () => {
    if (!toDelete) return;
    setInternalTeams(teams.filter(team => team.id !== toDelete.id));
    setToDelete(null);
  };
  return <div className="space-y-6" data-id="element-4208" data-mp="2l13brnzf6i50">
      <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-4209" data-mp="272vibq1ldyyl">
        <div data-id="element-4210" data-mp="275rcd3abk3hr">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-4211" data-mp="2c284e8kn3204">{t('Équipes internes', 'Internal teams')}</h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-4212" data-mp="1y38e6whhaf1u">
            {t('Regroupez des utilisateurs pour les cibler comme critère « Qui » dans un avis.', 'Group users to target them as a “Who” criterion in a notice.')}
          </p>
        </div>
        <button type="button" onClick={() => setForm({
        open: true,
        team: null
      })} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-indigo-700" data-id="element-4213" data-mp="cynkphwtv4w">
          <PlusIcon className="w-4 h-4" data-id="element-4214" data-mp="14997p2roxhrd" />
          {t('Nouvelle équipe', 'New team')}
        </button>
      </div>

      {teams.length === 0 ? <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-id="element-4215" data-mp="vadzxm8zzzly">
          <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-indigo-50 text-[#4F46E5] mb-3" data-id="element-4216" data-mp="1s054yfex1560">
            <UsersIcon className="w-5 h-5" data-id="element-4217" data-mp="1nzupmc58h2eh" />
          </span>
          <p className="text-sm font-medium text-slate-700" data-id="element-4218" data-mp="1j8396ufskd22">{t('Aucune équipe', 'No team yet')}</p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-4219" data-mp="1e903d7rzm2t1">
            {t('Créez une équipe pour la proposer dans l’audience.', 'Create a team to offer it in the audience.')}
          </p>
        </div> : <ul className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 shadow-sm" data-id="element-4220" data-mp="26xxkv8odjvkq">
          {teams.map(team => {
        const members = STAFF_MEMBERS.filter(m => team.memberIds.includes(m.id));
        const shown = members.slice(0, 4);
        return <li key={team.id} className="group flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 cursor-pointer" onClick={() => setForm({
          open: true,
          team
        })} data-id="element-4221" data-mp="2o7868vbylm8c">
                <span className="w-9 h-9 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center flex-shrink-0" data-id="element-4222" data-mp="2x6mwpus4lwsu">
                  <UsersIcon className="w-4 h-4" data-id="element-4223" data-mp="324wt28pl73g1" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-4224" data-mp="36yivz95y7syn">
                  <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-4225" data-mp="36zu1bdf1l8rv">{team.name}</p>
                  <p className="text-xs text-slate-500 truncate" data-id="element-4226" data-mp="3bydmvcpq4ijp">
                    {members.map(m => m.name).join(', ')}
                  </p>
                </div>
                <div className="hidden sm:flex items-center -space-x-2" aria-hidden="true" data-id="element-4227" data-mp="3kxs9u26omazg">
                  {shown.map(m => <span key={m.id} title={m.name} className="w-7 h-7 rounded-full bg-slate-100 border-2 border-white text-[10px] font-semibold text-slate-600 flex items-center justify-center" data-id="element-4228" data-mp="3785pwyyreirj">
                      {m.name.split(' ').map(p => p[0]).slice(0, 2).join('')}
                    </span>)}
                  {members.length > shown.length && <span className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white text-[10px] font-semibold text-slate-600 flex items-center justify-center" data-id="element-4229" data-mp="2ybewyvfi7k16">
                      +{members.length - shown.length}
                    </span>}
                </div>
                <span className="text-xs text-slate-400 w-20 text-right flex-shrink-0" data-id="element-4230" data-mp="8jqf59mz33c2">
                  {members.length} {members.length > 1 ? t('membres', 'members') : t('membre', 'member')}
                </span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-4231" data-mp="1vij4fm6d7wp7">
                  <button type="button" onClick={e => {
              e.stopPropagation();
              setForm({
                open: true,
                team
              });
            }} aria-label={`${t('Modifier', 'Edit')} ${team.name}`} className="p-1.5 rounded-md text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50" data-id="element-4232" data-mp="1mg9vh0qqwbdx">
                    <PencilIcon className="w-4 h-4" data-id="element-4233" data-mp="25evv0b6m5ufq" />
                  </button>
                  <button type="button" onClick={e => {
              e.stopPropagation();
              setToDelete(team);
            }} aria-label={`${t('Supprimer', 'Delete')} ${team.name}`} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" data-id="element-4234" data-mp="2t500yp79y69w">
                    <TrashIcon className="w-4 h-4" data-id="element-4235" data-mp="2xtiw82s8z189" />
                  </button>
                </div>
              </li>;
      })}
        </ul>}

      <InternalTeamFormDialog open={form.open} team={form.open ? form.team : null} onClose={closeForm} onSave={save} data-id="element-4236" data-mp="2stwna5jgc759" />

      <ConfirmDeleteDialog open={Boolean(toDelete)} title={t('Supprimer cette équipe ?', 'Delete this team?')} message={toDelete && <>
              {t('L’équipe ', 'The team ')}
              <span className="font-semibold text-slate-700" data-id="element-4238" data-mp="2a5hvhg1b8p3e">« {toDelete.name} »</span>
              {t(' ne sera plus proposée comme critère d’audience. Les avis déjà envoyés ne sont pas modifiés.', ' will no longer be offered as an audience criterion. Notices already sent are not affected.')}
            </>} onCancel={cancelDelete} onConfirm={confirmDelete} data-id="element-4237" data-mp="2k1kxmlw5wnqc" />
    </div>;
}